import { useDispatch, useSelector } from "react-redux"
import { useEffect, useState } from "react"
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
  Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip
} from "@mui/material"
import { fetchWalletTransactions } from "../../../../../redux/specialist-slices/wallet.slice"

// Matches WalletTransaction.type enum exactly
const TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "credit_pending", label: "Pending Credit" },
  { value: "release_to_available", label: "Released" },
  { value: "payout", label: "Payout" },
  { value: "reversal", label: "Reversal" },
]

const statusConfig = {
  "Payment Held": {
    label: "Payment Secured (Held by Platform)",
    color: "warning",
  },

  "Ready for Payout": {
    label: "Awaiting Payout to You",
    color: "info",
  },

  "Paid to Specialist": {
    label: "Paid to You",
    color: "success",
  },

  "Eligible for Specialist Payout": {
    label: "Eligible for Payout to You",
    color: "primary",
  },

  "Refunded to Member": {
    label: "Refunded to Client (No payout to you)",
    color: "error",
  },

  "Partially Refunded to Member": {
    label: "Partially Refunded to Client (Your payout reduced)",
    color: "error",
  },

  "Refunded to Member (Specialist No-Show)": {
    label: "Client Refunded (Specialist No-Show)",
    color: "error",
  },

  "Partially Refunded to Member (Specialist No-Show)": {
    label: "Partial Refund to Client (No-Show, payout reduced)",
    color: "error",
  },

  "Payment Blocked": {
    label: "Payout Blocked (Under Review)",
    color: "default",
  },

  "Awaiting Payment": {
    label: "Payment Pending from Client",
    color: "default",
  },

  Processing: {
    label: "Processing Transaction",
    color: "default",
  },
};

function TransactionsTab({ onboardingComplete }) {
  const dispatch = useDispatch()
  const { transactions, transactionsPagination, transactionsLoading, transactionsError } =
    useSelector((s) => s.walletSlice)
  const [page, setPage] = useState(1)
  const [typeFilter, setTypeFilter] = useState("")

  useEffect(() => {
    dispatch(
      fetchWalletTransactions({ page, limit: 15, type: typeFilter || null })
    )
  }, [page, typeFilter, dispatch])

  const totalPages = transactionsPagination?.totalPages || 1

  if (!onboardingComplete) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="text.secondary" fontSize="14px">
          Transactions will appear here once your account is fully verified.
        </Typography>
      </Box>
    )
  }

  return (
    <Box>
      {/* Filter bar */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <Select
          size="small"
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
          displayEmpty
          sx={{
            fontSize: "13px",
            borderRadius: "8px",
            minWidth: 160,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e5e7eb" },
          }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "13px" }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>

        <Typography fontSize="12px" color="text.secondary" ml="auto">
          {transactionsPagination?.totalCount ?? 0} transaction(s)
        </Typography>
      </Box>

      {/* List */}
      {/* {transactionsLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={32} />
        </Box>
      ) : transactionsError ? (
        <Box sx={{ p: 3, bgcolor: "#fff5f5", borderRadius: "10px", color: "#b91c1c", fontSize: "13px" }}>
          {transactionsError}
        </Box>
      ) : !transactions.length ? (
        <EmptyState message="No transactions found." />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {transactions.map((tx, i) => (
            <TransactionRow key={tx._id || i} tx={tx} />
          ))}
        </Box>
      )} */}


      {/* List */}
      {transactionsLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress size={32} />
        </Box>
      ) : transactionsError ? (
        <Box
          sx={{
            p: 3,
            bgcolor: "#fff5f5",
            borderRadius: "10px",
            color: "#b91c1c",
            fontSize: "13px",
          }}
        >
          {transactionsError}
        </Box>
      ) : !transactions.length ? (
        <EmptyState message="No transactions found." />
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell sx={{ fontWeight: 600 }}>Paid by</TableCell>
                {/* <TableCell sx={{ fontWeight: 600 }}>Type</TableCell> */}
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                {/* <TableCell sx={{ fontWeight: 600 }}>Available</TableCell> */}
                {/* <TableCell sx={{ fontWeight: 600 }}>Pending</TableCell> */}
                <TableCell sx={{ fontWeight: 600 }}>Currency</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {transactions.map((tx, index) => (
                <TableRow key={tx._id || index} hover>
                  {/* Triggered By */}
                  <TableCell>
                    <Stack spacing={0.3}>
                      <Typography fontWeight={500}>
                        {tx.triggeredBy?.fullName || "-"}
                      </Typography>

                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {tx.triggeredBy?.email}
                      </Typography>
                    </Stack>
                  </TableCell>
                  {/* Amount */}
                  <TableCell>
                    ${(tx.amount / 100).toFixed(2)}
                  </TableCell>

                  {/* Currency */}
                  <TableCell>
                    {tx.currency?.toUpperCase()}
                  </TableCell>

                  {/* Date */}
                  <TableCell>
                    {
                      new Date(tx.createdAt).toLocaleDateString("en-US", {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                      }).replaceAll("/", "-")
                    }
                  </TableCell>

                  {/* Status */}
                  {/* <TableCell>
                    <Chip
                      label={
                        tx.type === "credit_pending"
                          ? "PENDING CREDIT"
                          : tx.type === "release_to_available"
                            ? "RELEASED"
                            : tx.type === "payout"
                              ? "PAID OUT"
                              : "REVERSED"
                      }
                      color={
                        tx.type === "credit_pending"
                          ? "warning"
                          : tx.type === "release_to_available"
                            ? "success"
                            : tx.type === "payout"
                              ? "primary"
                              : "error"
                      }
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell> */}
                  <TableCell>
                    <Chip
                      label={
                        statusConfig[tx.specialistPaymentStatus]?.label ||
                        tx.specialistPaymentStatus ||
                        "N/A"
                      }
                      color={
                        statusConfig[tx.specialistPaymentStatus]?.color || "default"
                      }
                      size="small"
                      sx={{
                        fontWeight: 600,
                        minWidth: 140,
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 3 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            sx={{ borderRadius: "8px", textTransform: "none", minWidth: 80 }}
          >
            Previous
          </Button>
          <Box
            sx={{
              px: 2,
              display: "flex",
              alignItems: "center",
              fontSize: "13px",
              color: "text.secondary",
            }}
          >
            Page {page} / {totalPages}
          </Box>
          <Button
            size="small"
            variant="outlined"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            sx={{ borderRadius: "8px", textTransform: "none", minWidth: 80 }}
          >
            Next
          </Button>
        </Box>
      )}
    </Box>


  )
}

function EmptyState({ message }) {
  return (
    <Box
      sx={{
        py: 7,
        textAlign: "center",
        borderRadius: "12px",
        border: "1.5px dashed #ddd",
        color: "text.disabled",
      }}
    >
      <Typography fontSize="14px">{message}</Typography>
    </Box>
  )
}

export default TransactionsTab
